import React, { Dispatch, SetStateAction, useState } from "react"
import EditSidebar from "../../Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "../../Ui/EditSidebar/EditSidebarHeader"
import { AddressType } from "lib/types/order"
import {
  OrderAddress,
  OrderAddressesModificationType,
  OrderAddressesModificationUpdateDataAddress
} from "lib/types/generated/graphql-types"
import { useForm } from "react-hook-form"
import Input from "../../Ui/Form/Input"
import PrimaryButton from "../../Ui/Buttons/PrimaryButton"
import { useMutation } from "@apollo/client"
import CREATE_ADDRESS_MODIFICATION from "../../../graphql/mutations/order/modification/address/CreateModificationAddresses"
import { useDispatch } from "react-redux"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { handleErrorMessages } from "helpers/errors"
import { ReactComponent as CopyIcon } from "images/icons/copy.svg"
import { CopyAddressButton } from "./ModifyAddress.styled"
import CheckboxSimple from "../../Ui/Form/CheckboxSimple"

type Props = {
  addressType: AddressType
  address: OrderAddress
  oppositeAddress: OrderAddress
  orderId: string
  refetch: () => void
  setShowPopup: Dispatch<SetStateAction<boolean>>
}

const ModifyAddress = ({
  addressType,
  address,
  oppositeAddress,
  orderId,
  refetch,
  setShowPopup
}: Props) => {
  const title =
    addressType === AddressType.SHIPPING_ADDRESS ? "Edit Shipping address" : "Edit Billing address"
  const dispatch = useDispatch()
  const [updateBothAddresses, setUpdateBothAddresses] = useState(false)
  const [createUpdateAddress, { loading }] = useMutation(CREATE_ADDRESS_MODIFICATION, {
    onError: (error) => handleErrorMessages(error),
    onCompleted: () => {
      refetch()
      dispatch(hideEditSidebar())
      setShowPopup(true)
    }
  })

  const { register, handleSubmit, reset } = useForm<OrderAddressesModificationUpdateDataAddress>()

  const handleCopyAddress = () => {
    reset({
      givenName: oppositeAddress.givenName ?? "",
      familyName: oppositeAddress.familyName ?? "",
      email: oppositeAddress.email ?? "",
      streetAddress: oppositeAddress.streetAddress ?? "",
      streetAddress2: oppositeAddress.streetAddress2 ?? "",
      postalCode: oppositeAddress.postalCode ?? "",
      stateOrProvince: oppositeAddress.stateOrProvince ?? "",
      city: oppositeAddress.city ?? "",
      country: oppositeAddress.country ?? "",
      telephoneNumber: oppositeAddress.telephoneNumber ?? "",
      pcc: oppositeAddress.pcc ?? ""
    })
  }

  const onSubmit = (data: OrderAddressesModificationUpdateDataAddress) => {
    const addressData = updateBothAddresses
      ? JSON.stringify({ shippingAddress: data, billingAddress: data })
      : addressType === AddressType.SHIPPING_ADDRESS
        ? JSON.stringify({ shippingAddress: data })
        : JSON.stringify({ billingAddress: data })

    createUpdateAddress({
      variables: {
        orderId: orderId,
        input: {
          addresses: {
            data: addressData,
            modificationType: OrderAddressesModificationType.Update
          }
        }
      }
    })
  }

  const oppositeLabel =
    addressType === AddressType.SHIPPING_ADDRESS ? "billing" : "shipping"

  return (
    <EditSidebar>
      <form onSubmit={handleSubmit(onSubmit)}>
        <EditSidebarHeader title={title}>
          <PrimaryButton type={"submit"} loading={loading}>
            Update
          </PrimaryButton>
        </EditSidebarHeader>
        <CopyAddressButton type="button" onClick={handleCopyAddress}>
          <CopyIcon />
          Copy from {oppositeLabel} address
        </CopyAddressButton>
        <CheckboxSimple
          name="updateBothAddresses"
          label={`Also update ${oppositeLabel} address`}
          value={updateBothAddresses}
          setValue={setUpdateBothAddresses}
        />
        <Input
          label={"Given name"}
          name={"givenName"}
          register={register}
          defaultValue={address.givenName ?? ""}
        ></Input>
        <Input
          label={"Family name"}
          name={"familyName"}
          register={register}
          defaultValue={address.familyName ?? ""}
        />
        <Input
          label={"Email"}
          name={"email"}
          register={register}
          defaultValue={address.email ?? ""}
        />
        <Input
          label={"Street address"}
          name={"streetAddress"}
          register={register}
          defaultValue={address.streetAddress ?? ""}
        />
        <Input
          label={"Street address 2"}
          name={"streetAddress2"}
          register={register}
          defaultValue={address.streetAddress2 ?? ""}
        />
        <Input
          label={"Postal code"}
          name={"postalCode"}
          register={register}
          defaultValue={address.postalCode ?? ""}
        />
        <Input
          label={"State or province"}
          name={"stateOrProvince"}
          register={register}
          defaultValue={address.stateOrProvince ?? ""}
        />
        <Input label={"City"} name={"city"} register={register} defaultValue={address.city ?? ""} />
        <Input
          label={"Country"}
          name={"country"}
          register={register}
          defaultValue={address.country ?? ""}
        />
        <Input
          label={"Telephone number"}
          name={"telephoneNumber"}
          register={register}
          defaultValue={address.telephoneNumber ?? ""}
        />
        <Input label={"PCC"} name={"pcc"} register={register} defaultValue={address.pcc ?? ""} />
      </form>
    </EditSidebar>
  )
}

export default ModifyAddress
